import { DiscordActivityPlatformApi, Platform } from '@tofu/platform';
import { AuthController } from '../../types/auth-controller';
import { createJwt } from '../../types/jwt';
import { safe } from 'safe-wrapper';
import { AuthTokens } from '../../types/auth-tokens';
import { FanforgeApiClient } from '@hotpot-arcade/fanforge-api-client';
import { AuthClient } from '../../auth/create-auth';

export class DiscordActivityAuthController implements AuthController {
  platform = Platform.DiscordActivity;

  constructor(private platformApi: DiscordActivityPlatformApi) {}

  async getHotpotAuthTokens(apiClient: FanforgeApiClient, authClient: AuthClient): Promise<AuthTokens> {
    const FEATURE_FLAG_BETTER_AUTH_ENABLED: boolean = true; // so we can quickly flip it back if there's an issue
    const { discordClientId, discordSdk } = this.platformApi;

    if (FEATURE_FLAG_BETTER_AUTH_ENABLED) {
      /**
       *
       *
       * New Auth Flow
       *
       *
       */
      const signinResponse = await authClient.discordActivitySignIn({ discordSdk });
      if (!signinResponse || !signinResponse.ok) {
        throw new Error(`Failed to sign in`);
      }
      const token = await apiClient.auth.token.$post(
        {},
        {
          headers: {
            Authorization: `Bearer ${signinResponse.token}`,
          },
        }
      );
      if (!token.ok) {
        throw new Error(`Failed to get auth tokens`);
      }
      const { jwt, accessToken } = await token.json();
      const authTokens = AuthTokens.new(createJwt(jwt), accessToken);
      await this._subscribeToDiscordEvents(authTokens, apiClient);

      return authTokens;
    } else {
      /**
       *
       *
       * Old Auth Flow
       *
       *
       */
      // *** Discord SDK Ready ***
      const [readyError, _] = await safe(() => discordSdk.ready())();
      if (readyError) throw new Error('DiscordAuthProvider: Error initializing Discord SDK: ' + readyError.message);
      console.log('Discord SDK Ready');
      // *** Discord SDK Authorize (obtain code) ***
      const [authorizeError, code] = await safe(async () => {
        return (
          await discordSdk.commands.authorize({
            client_id: discordClientId,
            response_type: 'code',
            state: '',
            prompt: 'none',
            /**
             * App permissions:
             * - know what servers you're in
             * - see who's talking in voice channels
             * - read your member info for servers you belong to
             * - access your username, avatar and banner
             * - create commands
             * - send you DMs
             */
            scope: ['identify', 'rpc.voice.read', 'guilds', 'applications.commands', 'guilds.members.read'],
          })
        ).code;
      })();
      if (authorizeError)
        throw new Error('DiscordAuthProvider: Error authorizing Discord Session: ' + authorizeError.message);
      // *** Auth Broker Session (obtain JWT) ***
      const response = await apiClient.auth.discord.token.$post({
        json: {
          code,
          guildId: discordSdk.guildId,
          channelId: discordSdk.channelId,
          instanceId: discordSdk.instanceId,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to get auth tokens: ${response.statusText}`);
      }
      const responseBody = await response.json();
      const authTokens = AuthTokens.new(createJwt(responseBody.jwt), responseBody.accessToken);
      await this._subscribeToDiscordEvents(authTokens, apiClient);
      return authTokens;
    }
  }

  private async _subscribeToDiscordEvents(tokens: AuthTokens, apiClient: FanforgeApiClient): Promise<void> {
    const { discordSdk } = this.platformApi;
    const [authError] = await safe(() =>
      discordSdk.commands.authenticate({
        access_token: tokens.accessToken,
      })
    )();
    if (authError) {
      throw new Error(`Error authenticating SDK: ${authError.message}`);
    }

    const [subscribeError] = await safe(() =>
      discordSdk.subscribe('ENTITLEMENT_CREATE', async () => {
        try {
          await apiClient.entitlements.discord.processing.$post();
        } catch (e) {
          console.error('Error processing Discord entitlements', e);
        }
      })
    )();
    if (subscribeError) {
      throw new Error(`Error subscribing to Discord events: ${subscribeError.message}`);
    }
  }
}
